import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapContactAssignmentConfirmPayload,
  mapContactAssignmentConfirmResult,
  mapContactAssignmentPreviewPayload,
  mapContactAssignmentPreviewResult,
  mapContactSelectionTaskPayload,
  mapContactSelectionTaskResult,
  mapContactWorkbenchDetail,
  mapContactWorkbenchGiftCount,
  mapContactWorkbenchItem,
  mapContactWorkbenchListQueryParams,
  mapContactWorkbenchLatestRecordLabel,
  mapContactWorkbenchMetadata,
  mapContactWorkbenchNlQueryParsePayload,
  mapContactWorkbenchNlQueryParseResult,
  mapContactWorkbenchOrganizationOption,
  mapContactWorkbenchOrganizationUnitOption,
  mapContactWorkbenchOwnerOptions,
  mapContactWorkbenchOwnerOptionsQueryParams,
  mapContactWorkbenchRecordCreatePayload,
  mapContactWorkbenchRecordDeleteResult,
  mapContactWorkbenchRecordItem,
  mapContactWorkbenchSchoolBatchAssignmentConfirmPayload,
  mapContactWorkbenchSchoolBatchAssignmentConfirmResult,
  mapContactWorkbenchSchoolBatchAssignmentPreviewPayload,
  mapContactWorkbenchSchoolBatchAssignmentPreviewResult,
  type ContactGiftDeleteResultRaw,
  type ContactGiftItemRaw,
  type ContactGiftListPageRaw,
  type ContactGiftMutationResultRaw,
  type ContactGiftOptionRaw,
  type ContactGiftMutationRaw,
  mapContactWorkbenchTransferPayload,
  mapContactWorkbenchTemporaryTaskAssignment,
  mapContactWorkbenchUpdatePayload,
  type ContactWorkbenchDetailRaw,
  type ContactWorkbenchGiftCountRaw,
  type ContactWorkbenchListPageRaw,
  type ContactWorkbenchLatestRecordLabelRaw,
  type ContactWorkbenchMetadataRaw,
  type ContactWorkbenchOwnerOptionsRaw,
  type ContactWorkbenchTemporaryTaskAssignmentRaw,
  type ContactWorkbenchNlQueryParseResultRaw,
  type ContactWorkbenchOrganizationOptionRaw,
  type ContactWorkbenchOrganizationUnitOptionRaw,
  type ContactWorkbenchRecordItemRaw,
  type ContactWorkbenchRecordDeleteResultRaw,
  type ContactWorkbenchRecordListRaw,
  type ContactWorkbenchSchoolBatchAssignmentConfirmResultRaw,
  type ContactWorkbenchSchoolBatchAssignmentPreviewResultRaw,
  type ContactWorkbenchTransferResultRaw,
} from "../api/adapters";
import type {
  ContactAssignmentConfirmPayload,
  ContactAssignmentConfirmResult,
  ContactAssignmentPreviewPayload,
  ContactGiftMutationPayload,
  ContactGiftDeleteResult,
  ContactGiftItem,
  ContactGiftListPage,
  ContactGiftOption,
  ContactGiftMutationResult,
  ContactAssignmentPreviewResult,
  ContactWorkbenchCreateCollaborationTaskPayload,
  ContactWorkbenchCreateCollaborationTaskResult,
  ContactWorkbenchCollaborationContactCounts,
  ContactWorkbenchDetail,
  ContactWorkbenchGiftCount,
  ContactWorkbenchListPage,
  ContactWorkbenchLatestRecordLabel,
  ContactWorkbenchListFilters,
  ContactWorkbenchListParams,
  ContactWorkbenchMetadata,
  ContactWorkbenchNlQueryParseResult,
  ContactWorkbenchOrganizationOption,
  ContactWorkbenchOrganizationUnitOption,
  ContactWorkbenchOwnerOptions,
  ContactWorkbenchRecordCreatePayload,
  ContactWorkbenchRecordDeletePayload,
  ContactWorkbenchRecordDeleteResult,
  ContactWorkbenchRecordItem,
  ContactWorkbenchSchoolBatchAssignmentConfirmPayload,
  ContactWorkbenchSchoolBatchAssignmentConfirmResult,
  ContactWorkbenchSchoolBatchAssignmentPreviewPayload,
  ContactWorkbenchSchoolBatchAssignmentPreviewResult,
  ContactWorkbenchTransferPayload,
  ContactWorkbenchTransferResult,
  ContactWorkbenchTemporaryTaskAssignment,
  ContactWorkbenchTemporaryTaskHandlerPayload,
  ContactWorkbenchTemporaryTaskWithdrawPayload,
  ContactWorkbenchTemporaryTaskWithdrawResult,
  ContactWorkbenchUpdatePayload,
} from "../types";

const GIFT_MUTATION_REQUEST_CONFIG = {
  timeout: 90_000,
  "axios-retry": { retries: 0 },
};

/** 查询联系人工作台元数据。 */
export async function fetchContactWorkbenchMetadata(): Promise<ContactWorkbenchMetadata> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchMetadataRaw>>(
    "/crm/contact-workbench/metadata",
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人工作台元数据接口返回了无效数据");
  }
  return mapContactWorkbenchMetadata(response.data.data);
}

/** 查询联系人工作台分页列表。 */
export async function fetchContactWorkbenchList(
  params: ContactWorkbenchListParams,
): Promise<ContactWorkbenchListPage> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchListPageRaw>>(
    "/crm/contact-workbench/contacts",
    {
      params: mapContactWorkbenchListQueryParams(params),
      paramsSerializer: { indexes: null },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人工作台列表接口返回了无效数据");
  }
  return {
    items: (response.data.data.items ?? []).map(mapContactWorkbenchItem),
    total: response.data.data.total,
    page: response.data.data.page,
    pageSize: response.data.data.page_size,
  };
}

/** 按当前表格命中联系人查询归属销售列头候选（对齐任务池原负责人，非销售部全员）。 */
export async function fetchContactWorkbenchOwnerOptions(
  params: ContactWorkbenchListFilters,
): Promise<ContactWorkbenchOwnerOptions> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchOwnerOptionsRaw>>(
    "/crm/contact-workbench/owner-options",
    {
      params: mapContactWorkbenchOwnerOptionsQueryParams(params),
      paramsSerializer: { indexes: null },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人工作台归属销售候选接口返回了无效数据");
  }
  return mapContactWorkbenchOwnerOptions(response.data.data);
}

/** 一次查询协作联系人全部/电销/强跟数量。 */
export async function fetchContactWorkbenchCollaborationContactCounts(params: {
  collaborationScope?: "mine" | "all";
}): Promise<ContactWorkbenchCollaborationContactCounts> {
  const response = await httpClient.get<
    ApiEnvelope<{
      all: number;
      tele_sales: number;
      strong_follow: number;
    }>
  >("/crm/contact-workbench/collaboration-contact-counts", {
    params: {
      collaboration_scope: params.collaborationScope ?? "mine",
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("协作联系人数量接口返回了无效数据");
  }
  return {
    all: response.data.data.all,
    teleSales: response.data.data.tele_sales,
    strongFollow: response.data.data.strong_follow,
  };
}

/** 异步批量查询当前页联系人最新动态标签。 */
export async function fetchLatestContactRecordLabels(
  contactIds: number[],
  viewScope?: "mine" | "all",
): Promise<ContactWorkbenchLatestRecordLabel[]> {
  if (!contactIds.length) return [];
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchLatestRecordLabelRaw[]>>(
    "/crm/contact-workbench/latest-record-labels",
    {
      params: {
        contact_ids: [...new Set(contactIds)],
        ...(viewScope ? { view_scope: viewScope } : {}),
      },
      paramsSerializer: { indexes: null },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人动态标签接口返回了无效数据");
  }
  return response.data.data.map(mapContactWorkbenchLatestRecordLabel);
}

/** 异步批量查询当前页联系人礼品数量。 */
export async function fetchContactGiftCounts(
  contactIds: number[],
  viewScope?: "mine" | "all",
): Promise<ContactWorkbenchGiftCount[]> {
  if (!contactIds.length) return [];
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchGiftCountRaw[]>>(
    "/crm/contact-workbench/gift-counts",
    {
      params: {
        contact_ids: [...new Set(contactIds)],
        ...(viewScope ? { view_scope: viewScope } : {}),
      },
      paramsSerializer: { indexes: null },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人礼品数量接口返回了无效数据");
  }
  return response.data.data.map(mapContactWorkbenchGiftCount);
}

/** 解析联系人工作台自然语言查询。 */
export async function parseContactWorkbenchNlQuery(
  query: string,
): Promise<ContactWorkbenchNlQueryParseResult> {
  const response = await httpClient.post<ApiEnvelope<ContactWorkbenchNlQueryParseResultRaw>>(
    "/crm/contact-workbench/nl-query/parse",
    mapContactWorkbenchNlQueryParsePayload(query),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人智能查询解析接口返回了无效数据");
  }
  return mapContactWorkbenchNlQueryParseResult(response.data.data);
}

/** 批量转移联系人归属销售。 */
export async function transferContactWorkbenchOwner(
  payload: ContactWorkbenchTransferPayload,
): Promise<ContactWorkbenchTransferResult> {
  const response = await httpClient.post<ApiEnvelope<ContactWorkbenchTransferResultRaw>>(
    "/crm/contact-workbench/contacts/transfer-owner",
    mapContactWorkbenchTransferPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人归属销售转移接口返回了无效数据");
  }
  return {
    updatedCount: response.data.data.updated_count,
  };
}

/** 修改联系人临时任务处理人；不传处理人时取消并收回任务。 */
export async function updateContactTemporaryTaskHandler({
  contactId,
  itemId,
  handlerUserId,
}: ContactWorkbenchTemporaryTaskHandlerPayload): Promise<ContactWorkbenchTemporaryTaskAssignment> {
  const response = await httpClient.put<ApiEnvelope<ContactWorkbenchTemporaryTaskAssignmentRaw>>(
    `/crm/contact-workbench/contacts/${contactId}/temporary-tasks/${itemId}`,
    { handler_user_id: handlerUserId },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人临时任务处理人更新接口返回了无效数据");
  }
  return mapContactWorkbenchTemporaryTaskAssignment(response.data.data);
}

/** 收回联系人尚未被认领的临时任务。 */
export async function withdrawContactTemporaryTask({
  contactId,
  itemId,
}: ContactWorkbenchTemporaryTaskWithdrawPayload): Promise<ContactWorkbenchTemporaryTaskWithdrawResult> {
  const response = await httpClient.delete<ApiEnvelope<{ withdrawn_count: number }>>(
    `/crm/contact-workbench/contacts/${contactId}/temporary-tasks/${itemId}`,
    { timeout: 60_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人临时任务收回接口返回了无效数据");
  }
  return { withdrawnCount: response.data.data.withdrawn_count };
}

/** 预览联系人主销售/共享销售分配影响。 */
export async function previewContactAssignment(
  payload: ContactAssignmentPreviewPayload,
): Promise<ContactAssignmentPreviewResult> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    "/crm/object-assignment/contacts/preview",
    mapContactAssignmentPreviewPayload(payload),
  );
  if (response.data.code !== "OK") {
    throw new Error("联系人分配预览接口返回了无效数据");
  }
  return mapContactAssignmentPreviewResult(response.data.data);
}

/** 确认联系人主销售/共享销售分配。 */
export async function confirmContactAssignment(
  payload: ContactAssignmentConfirmPayload,
): Promise<ContactAssignmentConfirmResult> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    "/crm/object-assignment/contacts/confirm",
    mapContactAssignmentConfirmPayload(payload),
  );
  if (response.data.code !== "OK") {
    throw new Error("联系人分配确认接口返回了无效数据");
  }
  return mapContactAssignmentConfirmResult(response.data.data);
}

/** 将联系人工作台已选联系人投放到协作任务池。 */
export async function createContactWorkbenchCollaborationTask(
  payload: ContactWorkbenchCreateCollaborationTaskPayload,
): Promise<ContactWorkbenchCreateCollaborationTaskResult> {
  const response = await httpClient.post<ApiEnvelope<unknown>>(
    "/crm/collaboration/tasks/from-contact-selection",
    mapContactSelectionTaskPayload(payload),
  );
  if (response.data.code !== "OK") {
    throw new Error("联系人投放任务池接口返回了无效数据");
  }
  return mapContactSelectionTaskResult(response.data.data);
}

/** 查询联系人详情。 */
export async function fetchContactWorkbenchDetail(
  contactId: number,
): Promise<ContactWorkbenchDetail> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchDetailRaw>>(
    `/crm/contact-workbench/contacts/${contactId}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人详情接口返回了无效数据");
  }
  return mapContactWorkbenchDetail(response.data.data);
}

/** 搜索联系人编辑可选择的学校. */
export async function searchContactWorkbenchOrganizations({
  keyword,
  provinces,
  limit = 50,
}: {
  keyword?: string;
  provinces?: string[];
  limit?: number;
}): Promise<ContactWorkbenchOrganizationOption[]> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchOrganizationOptionRaw[]>>(
    "/crm/contact-workbench/organizations",
    {
      params: { keyword, provinces, limit },
      paramsSerializer: { indexes: null },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人学校候选项接口返回了无效数据");
  }
  return response.data.data.map(mapContactWorkbenchOrganizationOption);
}

/** 搜索当前区域负责人可批量分配的学校. */
export async function searchContactWorkbenchSchoolBatchAssignmentOrganizations({
  keyword,
  limit = 50,
}: {
  keyword?: string;
  limit?: number;
}): Promise<ContactWorkbenchOrganizationOption[]> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchOrganizationOptionRaw[]>>(
    "/crm/contact-workbench/school-batch-assignment/organizations",
    { params: { keyword, limit } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("区域学校候选项接口返回了无效数据");
  }
  return response.data.data.map(mapContactWorkbenchOrganizationOption);
}

/** 读取当前区域负责人省市批量选择所需的完整学校范围. */
export async function fetchContactWorkbenchSchoolBatchAssignmentOrganizationScope(): Promise<
  ContactWorkbenchOrganizationOption[]
> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchOrganizationOptionRaw[]>>(
    "/crm/contact-workbench/school-batch-assignment/organization-scope",
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("区域学校完整范围接口返回了无效数据");
  }
  return response.data.data.map(mapContactWorkbenchOrganizationOption);
}

/** 统计所选学校涉及的联系人数量. */
export async function previewContactWorkbenchSchoolBatchAssignment(
  payload: ContactWorkbenchSchoolBatchAssignmentPreviewPayload,
): Promise<ContactWorkbenchSchoolBatchAssignmentPreviewResult> {
  const response = await httpClient.post<
    ApiEnvelope<ContactWorkbenchSchoolBatchAssignmentPreviewResultRaw>
  >(
    "/crm/contact-workbench/school-batch-assignment/preview",
    mapContactWorkbenchSchoolBatchAssignmentPreviewPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("按学校批量分配范围接口返回了无效数据");
  }
  return mapContactWorkbenchSchoolBatchAssignmentPreviewResult(response.data.data);
}

/** 确认按学校批量分配联系人. */
export async function confirmContactWorkbenchSchoolBatchAssignment(
  payload: ContactWorkbenchSchoolBatchAssignmentConfirmPayload,
): Promise<ContactWorkbenchSchoolBatchAssignmentConfirmResult> {
  const response = await httpClient.post<
    ApiEnvelope<ContactWorkbenchSchoolBatchAssignmentConfirmResultRaw>
  >(
    "/crm/contact-workbench/school-batch-assignment/confirm",
    mapContactWorkbenchSchoolBatchAssignmentConfirmPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("按学校批量分配确认接口返回了无效数据");
  }
  return mapContactWorkbenchSchoolBatchAssignmentConfirmResult(response.data.data);
}

/** 搜索联系人编辑可选择的院系专业. */
export async function searchContactWorkbenchOrganizationUnits({
  orgId,
  keyword,
  limit = 50,
}: {
  orgId: number;
  keyword?: string;
  limit?: number;
}): Promise<ContactWorkbenchOrganizationUnitOption[]> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchOrganizationUnitOptionRaw[]>>(
    `/crm/contact-workbench/organizations/${orgId}/units`,
    {
      params: { keyword, limit },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人院系专业候选项接口返回了无效数据");
  }
  return response.data.data.map(mapContactWorkbenchOrganizationUnitOption);
}

/** 更新联系人资料。 */
export async function updateContactWorkbenchDetail({
  contactId,
  payload,
}: {
  contactId: number;
  payload: ContactWorkbenchUpdatePayload;
}): Promise<ContactWorkbenchDetail> {
  const response = await httpClient.put<ApiEnvelope<ContactWorkbenchDetailRaw>>(
    `/crm/contact-workbench/contacts/${contactId}`,
    mapContactWorkbenchUpdatePayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人更新接口返回了无效数据");
  }
  return mapContactWorkbenchDetail(response.data.data);
}

/** 新增联系人。 */
export async function createContactWorkbenchContact(
  payload: ContactWorkbenchUpdatePayload,
): Promise<ContactWorkbenchDetail> {
  const response = await httpClient.post<ApiEnvelope<ContactWorkbenchDetailRaw>>(
    "/crm/contact-workbench/contacts",
    mapContactWorkbenchUpdatePayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人新增接口返回了无效数据");
  }
  return mapContactWorkbenchDetail(response.data.data);
}

/** 查询联系人动态列表。 */
export async function fetchContactWorkbenchRecords(
  contactId: number,
): Promise<ContactWorkbenchRecordItem[]> {
  const response = await httpClient.get<ApiEnvelope<ContactWorkbenchRecordListRaw>>(
    `/crm/contact-workbench/contacts/${contactId}/records`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人动态列表接口返回了无效数据");
  }
  return (response.data.data.items ?? []).map(mapContactWorkbenchRecordItem);
}

/** 新增联系人动态。 */
export async function createContactWorkbenchRecord({
  contactId,
  payload,
}: {
  contactId: number;
  payload: ContactWorkbenchRecordCreatePayload;
}): Promise<ContactWorkbenchRecordItem> {
  const response = await httpClient.post<ApiEnvelope<ContactWorkbenchRecordItemRaw>>(
    `/crm/contact-workbench/contacts/${contactId}/records`,
    mapContactWorkbenchRecordCreatePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人动态新增接口返回了无效数据");
  }
  return mapContactWorkbenchRecordItem(response.data.data);
}

/** 删除联系人动态。 */
export async function deleteContactWorkbenchRecord({
  contactId,
  recordId,
  currentUserId,
}: ContactWorkbenchRecordDeletePayload): Promise<ContactWorkbenchRecordDeleteResult> {
  const response = await httpClient.delete<ApiEnvelope<ContactWorkbenchRecordDeleteResultRaw>>(
    `/crm/contact-workbench/contacts/${contactId}/records/${recordId}`,
    {
      params: { current_user_id: currentUserId },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人动态删除接口返回了无效数据");
  }
  return mapContactWorkbenchRecordDeleteResult(response.data.data);
}

/** 新增联系人礼品记录。 */
export async function createContactGift(
  contactId: number,
  payload: ContactGiftMutationPayload,
): Promise<ContactGiftMutationResult> {
  const body: ContactGiftMutationRaw = {
    request_id: payload.requestId,
    gift_type: payload.giftType,
    sent_at: payload.sentAt,
    send_remark: payload.sendRemark,
    receive_remark: payload.receiveRemark,
    amount: payload.amount,
    details: payload.details.map((detail) => ({
      gift_option_id: detail.giftOptionId,
      gift_name: detail.giftName,
      quantity: detail.quantity,
      unit_price: detail.unitPrice,
    })),
  };
  const response = await httpClient.post<
    ApiEnvelope<{ gift_id: string; created: boolean }>
  >(
    `/crm/contact-workbench/contacts/${contactId}/gifts`,
    body,
    GIFT_MUTATION_REQUEST_CONFIG,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人礼品新增接口返回了无效数据");
  }
  return {
    giftId: response.data.data.gift_id,
    created: response.data.data.created,
  };
}

/** 查询单个联系人的旧系统礼品记录。 */
export async function fetchContactGifts(
  contactId: number,
  page = 1,
  pageSize = 20,
): Promise<ContactGiftListPage> {
  const response = await httpClient.get<ApiEnvelope<ContactGiftListPageRaw>>(
    `/crm/contact-workbench/contacts/${contactId}/gifts`,
    { params: { page, page_size: pageSize } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人礼品列表接口返回了无效数据");
  }
  return {
    items: (response.data.data.items ?? []).map(mapContactGiftItem),
    total: response.data.data.total,
    page: response.data.data.page,
    pageSize: response.data.data.page_size,
  };
}

/** 查询旧系统有效礼品字典。 */
export async function fetchContactGiftOptions(): Promise<ContactGiftOption[]> {
  const response = await httpClient.get<ApiEnvelope<ContactGiftOptionRaw[]>>(
    "/crm/contact-workbench/gift-options",
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人礼品字典接口返回了无效数据");
  }
  return response.data.data.map((item) => ({
    giftOptionId: item.gift_option_id,
    giftName: item.gift_name,
    unitPrice: item.unit_price ?? undefined,
  }));
}

/** 更新一条可编辑的联系人礼品记录。 */
export async function updateContactGift(
  contactId: number,
  giftId: string,
  payload: ContactGiftMutationPayload,
): Promise<ContactGiftMutationResult> {
  const response = await httpClient.put<ApiEnvelope<ContactGiftMutationResultRaw>>(
    `/crm/contact-workbench/contacts/${contactId}/gifts/${giftId}`,
    mapContactGiftMutation(payload),
    GIFT_MUTATION_REQUEST_CONFIG,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人礼品更新接口返回了无效数据");
  }
  return { giftId: response.data.data.gift_id, created: response.data.data.created };
}

/** 停用一条联系人礼品记录。 */
export async function deleteContactGift(
  contactId: number,
  giftId: string,
): Promise<ContactGiftDeleteResult> {
  const response = await httpClient.delete<ApiEnvelope<ContactGiftDeleteResultRaw>>(
    `/crm/contact-workbench/contacts/${contactId}/gifts/${giftId}`,
    GIFT_MUTATION_REQUEST_CONFIG,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("联系人礼品删除接口返回了无效数据");
  }
  return { outcome: response.data.data.outcome };
}

/** 将前端礼品表单映射为后端契约。 */
function mapContactGiftMutation(payload: ContactGiftMutationPayload): ContactGiftMutationRaw {
  return {
    request_id: payload.requestId,
    gift_type: payload.giftType,
    sent_at: payload.sentAt,
    send_remark: payload.sendRemark,
    receive_remark: payload.receiveRemark,
    amount: payload.amount,
    details: payload.details.map((detail) => ({
      gift_option_id: detail.giftOptionId,
      gift_name: detail.giftName,
      quantity: detail.quantity,
      unit_price: detail.unitPrice,
    })),
  };
}

/** 将后端礼品明细映射为前端领域对象。 */
function mapContactGiftItem(item: ContactGiftItemRaw): ContactGiftItem {
  return {
    giftId: item.gift_id,
    giftType: item.gift_type,
    sentAt: item.sent_at ?? undefined,
    sendRemark: item.send_remark ?? undefined,
    receiveRemark: item.receive_remark ?? undefined,
    amount: item.amount ?? undefined,
    recordedByUserId: item.recorded_by_user_id ?? undefined,
    recordedByUserName: item.recorded_by_user_name ?? undefined,
    details: (item.details ?? []).map((detail) => ({
      detailId: detail.detail_id,
      giftOptionId: detail.gift_option_id ?? undefined,
      giftName: detail.gift_name,
      quantity: detail.quantity,
      unitPrice: detail.unit_price ?? undefined,
    })),
    canEdit: item.can_edit,
    canDelete: item.can_delete,
  };
}
